// packages/whiteboard/src/optionsbar/optionsbar-config.ts

import type { BoardObject } from '../core/interfaces/board-object.interface.js';
import { resolveStickyProperty, STICKY_OPTIONS } from '../features/sticky/sticky-options.config.js';
import { resolveLineProperty, LINE_OPTIONS } from '../features/line/line-options.config.js';
import { RECTANGLE_OPTIONS } from '../features/rectangle/rectangle-options.config.js';
import { ELLIPSE_OPTIONS } from '../features/ellipse/ellipse-options.config.js';
import { CHECKBOX_OPTIONS } from '../features/checkbox/checkbox-options.config.js';
import { PERSON_OPTIONS } from '../features/person/person-options.config.js';
import { SMILEY_OPTIONS } from '../features/smiley/smiley-options.config.js';
import { TEXTBOX_OPTIONS } from '../features/textbox/textbox-options.config.js';
import {
  type ActionIntent,
  type OptionsCircleDef,
  BOARD_COMMAND_ACTIONS,
  Z_ORDER_ACTIONS,
  ALIGN_ACTIONS,
} from './optionsbar-actions.js';
import { composeOptionsCircle } from './parts/compose-options-circle.js';

// Standard-Kreis (nichts ausgewählt) = nur die Standard-Knöpfe aus part1.
const defaultSvg = composeOptionsCircle([1]);

// Re-export shared types / action sets so existing import sites that
// pulled them from this module keep working. The actual definitions live
// in `optionsbar-actions.ts` (PRD-F1 §T31 cycle-break fix).
export type { ActionIntent, ActionsMap, OptionsCircleDef, SvgActionEntry } from './optionsbar-actions.js';
export { FILL_ACTIONS, BORDER_ACTIONS } from './optionsbar-actions.js';

// ---- Per-type circle registry ----------------------------------------

/**
 * Optionskreise je Objekttyp. `default` greift, wenn nichts ausgewählt
 * ist oder der Typ keinen eigenen Kreis hat. Wenn ein Typ hier nicht
 * eingetragen ist, fällt der Optionskreis auf `default` zurück.
 */
export const OPTIONS_CIRCLES: Partial<Record<BoardObject['type'] | 'default', OptionsCircleDef>> = {
  // PRD-F6 §TR12: textbox owns its OptionsCircleDef + intent → property contract.
  text: TEXTBOX_OPTIONS,
  // PRD-F1 §T27: rectangle/ellipse entries delegate to their slice-owned
  // OptionsCircleDef so the optionsbar-config no longer hard-codes per-shape
  // SVG/action wiring. Other shapes will follow as their slices land.
  rectangle: RECTANGLE_OPTIONS,
  ellipse: ELLIPSE_OPTIONS,
  // PRD-F2 §T29: checkbox/person delegate to their slice-owned
  // OptionsCircleDef so the optionsbar-config no longer hard-codes their
  // SVG/action wiring.
  // Checkbox: slice-owned circle (parts 1+3+9) — Füllfarbe, Größe (S/M/L)
  // und die drei Icon-Varianten (Stefan, 2026-08-29, siehe
  // checkbox-options.config.ts — löst das TEMP-Ausblenden von Anke,
  // 2026-07-12 endlich ab).
  checkbox: CHECKBOX_OPTIONS,
  person: PERSON_OPTIONS,
  // Smiley: slice-owned circle (parts 1+3+8) — 22 motif buttons + fill colour.
  smiley: SMILEY_OPTIONS,
  // Sticky note: slice-owned circle composed from shared action maps
  // (fill / z-order / board-command) — see sticky-options.config.ts.
  sticky: STICKY_OPTIONS,
  // Line: stroke colour/width/style + shadow, z-order and board commands.
  // End caps have no buttons in the circle and stay in the actions bar.
  line: LINE_OPTIONS,
  // Standardkreis: wird IMMER gezeigt (auch ohne Auswahl/Werkzeug). part1
  // trägt die Standard-Knöpfe — Zwischenablage/Verlauf/Löschen und die
  // Z-Reihenfolge. Ohne Auswahl sind Kopieren/Löschen/Z-Reihenfolge stille
  // No-Ops (brauchen ein Objekt); Rückgängig/Wiederholen wirken global.
  //
  // Dieser Kreis ist auch der, den eine MEHRFACHAUSWAHL zeigt (`pickCircleKey`
  // fällt ohne genau ein Objekt auf `default` zurück) — die Ausricht-Knöpfe
  // wirken also praktisch nur hier. Unter zwei Objekten tun sie still nichts,
  // wie Kopieren/Löschen ohne Auswahl (Anke, 2026-07-16).
  default: {
    svg: defaultSvg,
    actions: { ...BOARD_COMMAND_ACTIONS, ...Z_ORDER_ACTIONS, ...ALIGN_ACTIONS },
  },
};

/**
 * Intents die als Toggle auf Objekt-Ebene wirken: ein zweiter Klick auf den
 * bereits aktiven Wert löscht die Eigenschaft wieder.
 *
 * 'font-style' und 'text-decoration' stehen hier für Objekte, die ihr Label
 * ohne Editor zeichnen (Person). Bei einer Textbox wird beides vorher als
 * Abschnitts-Markierung abgefangen (SELECTION_FORMAT_INTENTS) und erreicht
 * diese Toggle-Logik nie. 'text-transform' und 'text-vertical-align' fehlen
 * bewusst — die kann bislang nur die Textbox.
 */
export const TOGGLE_INTENTS: ReadonlySet<ActionIntent> = new Set<ActionIntent>([
  'box-shadow',
  'background-opacity',
  'font-style',
  'text-decoration',
]);

/**
 * Linien-Enden. Wie TOGGLE_INTENTS ein Ein/Aus-Schalter — aber der zweite Klick
 * setzt `'none'` statt die Eigenschaft zu löschen: `startCap`/`endCap` sind
 * Pflichtfelder, die der Renderer bedingungslos liest. Der Optionskreis zeichnet
 * bewusst keinen eigenen „kein Ende"-Knopf; der aktive Knopf ist der Ausschalter.
 */
export const LINE_CAP_INTENTS: ReadonlySet<ActionIntent> = new Set<ActionIntent>([
  'line-cap-start',
  'line-cap-end',
]);

/**
 * Intents die bei einer TEXTBOX nur auf den markierten Text-Abschnitt im
 * Bearbeitungsmodus wirken. Das Board-Element leitet sie an
 * applyTextFormatToSelection weiter; außerhalb des Bearbeitungsmodus passiert
 * nichts. Für andere Objekttypen gelten sie als normale Objekt-Eigenschaft —
 * der Optionskreis prüft daher zusätzlich den Typ.
 */
export const SELECTION_FORMAT_INTENTS: ReadonlySet<ActionIntent> = new Set<ActionIntent>([
  'font-style',
  'text-decoration',
  'text-transform',
  'text-vertical-align',
]);

/**
 * Resolve an action intent for a specific object type to the concrete
 * BoardObject property name. Returns null if the intent doesn't apply
 * to that type (e.g. fill on a line) — callers should treat that as a
 * no-op rather than an error.
 *
 * Slice-owned mappings: each migrated slice resolves its own intent →
 * property contract via `OPTIONS_CIRCLES[type].resolveProperty`. The
 * fallback switch only retains the (non-migrated) rectangle/ellipse
 * mappings until those slices fully own their resolveProperty.
 */
export function resolveProperty(
  intent: ActionIntent,
  objType: BoardObject['type'],
): string | null {
  if (objType === 'sticky') {
    return resolveStickyProperty(intent);
  }
  if (objType === 'checkbox') {
    return CHECKBOX_OPTIONS.resolveProperty?.(intent) ?? null;
  }
  if (objType === 'person') {
    return PERSON_OPTIONS.resolveProperty?.(intent) ?? null;
  }
  if (objType === 'smiley') {
    return SMILEY_OPTIONS.resolveProperty?.(intent) ?? null;
  }
  if (objType === 'line') {
    return resolveLineProperty(intent);
  }
  // PRD-F6 §TR12: textbox owns its own intent → property mapping.
  if (objType === 'text') {
    return TEXTBOX_OPTIONS.resolveProperty?.(intent) ?? null;
  }
  switch (intent) {
    case 'fill':
      switch (objType) {
        case 'rectangle':
        case 'ellipse':                           return 'fill';
        default:                                  return null;
      }
    case 'border-color':
      switch (objType) {
        case 'rectangle':
        case 'ellipse':                           return 'strokeColor';
        default:                                  return null;
      }
    case 'border-width':
      switch (objType) {
        case 'rectangle':
        case 'ellipse':                           return 'strokeWidth';
        default:                                  return null;
      }
    case 'border-radius':
      switch (objType) {
        case 'rectangle':  return 'cornerRadius';
        default:           return null;
      }
    case 'border-style':
      return objType === 'rectangle' || objType === 'ellipse' ? 'strokeStyle' : null;
    case 'box-shadow':
      return objType === 'rectangle' || objType === 'ellipse' ? 'boxShadow' : null;
    case 'background-opacity':
      return objType === 'rectangle' || objType === 'ellipse' ? 'backgroundOpacity' : null;
    case 'z-order':
    case 'board-command':
      // Kein Property-Update — wird im Optionsbar-Element als Befehl behandelt.
      return null;
    default:
      return null;
  }
}
